/**
 * Types and thresholds for KPI cards
 */

/** Metric types that the KPI card can render. Every type always shows a badge. */
export const KPI_METRIC_TYPE = {
  /** 0–4 scale answer. Badge uses the 5-level scale interpretation. */
  SCALE: 'scale',
  /** Net Promoter Score, -100 to +100, rendered as a gauge track. Badge uses the 2-level good/needsAttention interpretation. */
  NPS: 'nps',
  /** Percentage, 0–100. Badge uses the 2-level good/needsAttention interpretation. */
  PERCENTAGE: 'percentage',
  /** Caller-defined metric with its own range and success/warning thresholds. Badge uses the 2-level good/needsAttention interpretation. */
  CUSTOM: 'custom',
} as const;

export type KpiMetricType = (typeof KPI_METRIC_TYPE)[keyof typeof KPI_METRIC_TYPE];

/**
 * Visual colour state shared by the bar fill and the score.
 * - `success` → green
 * - `warning` → orange
 * - `danger`  → red
 * - `normal`  → blue bar, black score (baseline, e.g. engagement)
 */
export type KpiState = 'success' | 'warning' | 'danger' | 'normal';

/** Interpretation levels for the `scale` metric's badge (best → worst). */
export type KpiScaleLevel = 'excellent' | 'good' | 'satisfactory' | 'weak' | 'inadequate';

/** Interpretation levels for the `nps` / `percentage` / `custom` metrics' badge. */
export type KpiBadgeLevel = 'good' | 'needsAttention';

/** Value range per metric, used for the bar fill / gauge position. */
export const KPI_RANGE: Record<KpiMetricType, { min: number; max: number }> = {
  [KPI_METRIC_TYPE.SCALE]: { min: 0, max: 4 },
  [KPI_METRIC_TYPE.NPS]: { min: -100, max: 100 },
  [KPI_METRIC_TYPE.PERCENTAGE]: { min: 0, max: 100 },
  /** Fallback range for `custom` — override via `min`/`max` on `KpiCard`/`evaluateKpi`. */
  [KPI_METRIC_TYPE.CUSTOM]: { min: 0, max: 100 },
};

/**
 * Scale interpretation thresholds (lower-inclusive bounds, highest first):
 * ≥ 3.7 excellent · 3.3–3.7 good · 3.0–3.3 satisfactory · 2.7–3.0 weak · < 2.7 inadequate.
 */
export const SCALE_LEVEL_THRESHOLDS: { level: KpiScaleLevel; from: number }[] = [
  { level: 'excellent', from: 3.7 },
  { level: 'good', from: 3.3 },
  { level: 'satisfactory', from: 3.0 },
  { level: 'weak', from: 2.7 },
  { level: 'inadequate', from: Number.NEGATIVE_INFINITY },
];

/** Colour state per scale level (5 levels → green / blue / orange). */
export const SCALE_LEVEL_STATE: Record<KpiScaleLevel, KpiState> = {
  excellent: 'success',
  good: 'success',
  satisfactory: 'normal',
  weak: 'warning',
  inadequate: 'warning',
};

export const NPS_THRESHOLDS = { successFrom: 30, warningFrom: 0 };

export const PERCENTAGE_THRESHOLDS = { successFrom: 60, warningFrom: 40 };

/**
 * Success/warning thresholds for the `custom` metric. Unlike the fixed thresholds
 * above, these are supplied by the caller (e.g. via `KpiCard`'s `successFrom`/`warningFrom` props).
 */
export type KpiCustomThresholds = { successFrom: number; warningFrom: number };
